import { addDays } from 'date-fns';
import { useMemo } from 'react';
import type { CalendarEvent, MultiDayEventSpan } from '../../../../types';

/**
 * Calculate multi-day all-day event spans for week mode
 * Groups events by ID and calculates which grid columns they should span
 */
export const useCalculateMultiDayEventSpans = <T extends CalendarEvent>({
  days,
  events,
}: {
  days: Date[];
  events: T[];
}): Array<MultiDayEventSpan<T>> =>
  useMemo(() => {
    const eventMap = new Map<number | string, T>();

    // Collect unique events
    for (const event of events) {
      if (event.isAllDay && !eventMap.has(event.id)) {
        eventMap.set(event.id, event);
      }
    }

    const spans: Array<MultiDayEventSpan<T>> = [];

    for (const event of eventMap.values()) {
      const daySpan = findEventDaySpan<T>({ days, event });

      if (daySpan) {
        spans.push({
          endDayIndex: daySpan.endDayIndex,
          event,
          startDayIndex: daySpan.startDayIndex,
        });
      }
    }

    return spans;
  }, [days, events]);

/**
 * Find the day span indices for a single event
 */
const findEventDaySpan = <T extends CalendarEvent>({
  days,
  event,
}: {
  days: Date[];
  event: T;
}): { endDayIndex: number; startDayIndex: number } | null => {
  const eventStart = new Date(event.start);
  eventStart.setHours(0, 0, 0, 0);
  const eventEnd = new Date(event.end);
  eventEnd.setHours(0, 0, 0, 0);
  let startDayIndex = -1;
  let endDayIndex = -1;

  for (const [index, day] of days.entries()) {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = addDays(dayStart, 1);

    // Check if event overlaps with this day
    if (event.start < dayEnd && event.end > dayStart) {
      if (startDayIndex === -1) {
        startDayIndex = index;
      }
      endDayIndex = index;
    }
  }

  if (startDayIndex === -1 || endDayIndex === -1) {
    return null;
  }

  return { endDayIndex, startDayIndex };
};
